import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import loginBackground from '../assets/brand/login-background.png';
import logoBlack from '../assets/brand/logo-black.png';
import logoWhiteWithText from '../assets/brand/logo-white-with-text.png';
import { getHostedUiUrl } from '../config/cognito';
import { useAuth } from './AuthContext';
const POST_LOGIN_REDIRECT_KEY = 'wc_portal_post_login_redirect';
export function peekPostLoginRedirect() {
    const value = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    if (value)
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    return value;
}
function authErrorMessage(err) {
    if (!(err instanceof Error))
        return 'Login failed';
    const code = err.code;
    if (code === 'InvalidPasswordException' || /Password did not conform/i.test(err.message)) {
        return 'Password does not meet requirements. Use at least 8 characters with upper, lower, number, and symbol.';
    }
    if (code === 'NotAuthorizedException') {
        return 'Incorrect email or password.';
    }
    if (code === 'UserNotFoundException') {
        return 'No account found for that email.';
    }
    return err.message || 'Login failed';
}
export function LoginPage() {
    const { login, finishNewPassword, cancelNewPassword, newPasswordEmail } = useAuth();
    const location = useLocation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const intendedPath = location.state?.from?.pathname || '/';
    const needsNewPassword = Boolean(newPasswordEmail);
    async function handleSubmit(e) {
        e.preventDefault();
        setError('');
        setSubmitting(true);
        try {
            const result = await login(email, password);
            if (result === 'newPasswordRequired') {
                setPassword('');
                setNewPassword('');
                setConfirmPassword('');
            }
        }
        catch (err) {
            setError(authErrorMessage(err));
        }
        finally {
            setSubmitting(false);
        }
    }
    async function handleNewPasswordSubmit(e) {
        e.preventDefault();
        setError('');
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }
        setSubmitting(true);
        try {
            await finishNewPassword(newPassword);
        }
        catch (err) {
            setError(authErrorMessage(err));
        }
        finally {
            setSubmitting(false);
        }
    }
    function handleBackToSignIn() {
        setError('');
        setNewPassword('');
        setConfirmPassword('');
        cancelNewPassword();
    }
    function handleHostedLogin() {
        if (intendedPath && intendedPath !== '/login') {
            sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, intendedPath);
        }
        const redirectUri = `${window.location.origin}/login`;
        window.location.href = getHostedUiUrl(redirectUri);
    }
    return (_jsxs("div", { className: "login-page", children: [_jsxs("section", { className: "login-visual", "aria-label": "Kokomo 24/7", children: [_jsx("img", { className: "login-visual-brand", src: logoWhiteWithText, alt: "Kokomo 24/7" }), _jsx("img", { className: "login-illustration", src: loginBackground, alt: "" })] }), _jsxs("section", { className: "login-panel", children: [_jsxs("div", { className: "login-card", children: [_jsxs("div", { className: "login-header", children: [_jsx("img", { src: logoBlack, alt: "", className: "login-logo" }), needsNewPassword ? (_jsxs(_Fragment, { children: [_jsx("h1", { children: "Set a new password" }), _jsxs("p", { children: ["Welcome", newPasswordEmail ? `, ${newPasswordEmail}` : '', ". Choose a permanent password to finish signing in."] })] })) : (_jsxs(_Fragment, { children: [_jsx("h1", { children: "Welcome to Kokomo 24/7\u00AE Safety Cloud\u2122" }), _jsx("p", { children: "Sign in to access the Behavioral Health Portal" })] }))] }), needsNewPassword ? (_jsxs("form", { onSubmit: handleNewPasswordSubmit, className: "login-form", children: [error && _jsx("div", { className: "alert alert-error", children: error }), _jsxs("label", { children: ["New password", _jsx("input", { type: "password", value: newPassword, onChange: (e) => setNewPassword(e.target.value), required: true, autoComplete: "new-password", placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022", autoFocus: true })] }), _jsxs("label", { children: ["Confirm password", _jsx("input", { type: "password", value: confirmPassword, onChange: (e) => setConfirmPassword(e.target.value), required: true, autoComplete: "new-password", placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022" })] }), _jsx("button", { type: "submit", className: "btn btn-primary btn-full", disabled: submitting, children: submitting ? 'Saving…' : 'Save password & continue' }), _jsx("button", { type: "button", className: "btn btn-secondary btn-full", onClick: handleBackToSignIn, disabled: submitting, children: "Back to sign in" })] })) : (_jsxs(_Fragment, { children: [_jsxs("form", { onSubmit: handleSubmit, className: "login-form", children: [error && _jsx("div", { className: "alert alert-error", children: error }), _jsxs("label", { children: ["Email address", _jsx("input", { type: "email", value: email, onChange: (e) => setEmail(e.target.value), required: true, autoComplete: "username", placeholder: "you@company.com" })] }), _jsxs("label", { children: ["Password", _jsx("input", { type: "password", value: password, onChange: (e) => setPassword(e.target.value), required: true, autoComplete: "current-password", placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022" })] }), _jsx("button", { type: "submit", className: "btn btn-primary btn-full", disabled: submitting, children: submitting ? 'Signing in…' : 'Sign in' })] }), _jsx("div", { className: "login-divider", children: _jsx("span", { children: "or" }) }), _jsx("button", { type: "button", className: "btn btn-secondary btn-full", onClick: handleHostedLogin, children: "Sign in with Cognito" })] }))] }), _jsxs("div", { className: "footer-need-help", children: [_jsx("span", { children: "Need Help?" }), ' ', _jsx("a", { href: "https://www.kokomo247.com/support?hsLang=en", target: "_blank", rel: "noreferrer", children: "Kokomo Support" })] })] })] }));
}
